import type { SocketStatus } from "~/context/call/callState";
import {
  countFlapsInWindow,
  latestFlapTimestamp,
} from "~/context/call/connectionHealth/flapTracker";
import type {
  ConnectionHealthSnapshot,
  ConnectionHealthStatus,
  ConnectionLayerHealth,
} from "~/context/call/connectionHealth/types";
import type { SipRegistrationStatus } from "~/services/calling/types";

const resolveSocketLayer = (
  socketStatus: SocketStatus,
  socketReconnecting: boolean
): ConnectionLayerHealth => {
  if (socketStatus === "connected") return "healthy";
  if (socketStatus === "connecting" || socketReconnecting) return "reconnecting";
  return "down";
};

const resolveSipLayer = (
  sipStatus: SipRegistrationStatus
): ConnectionLayerHealth => {
  if (sipStatus === "registered") return "healthy";
  if (sipStatus === "connecting") return "reconnecting";
  return "down";
};

export const resolveConnectionHealth = (params: {
  socketStatus: SocketStatus;
  sipStatus: SipRegistrationStatus;
  socketReconnecting: boolean;
  isNetworkOnline: boolean;
  socketFlapTimestamps: number[];
  sipFlapTimestamps: number[];
  layerDownSinceMs: { socket: number | null; sip: number | null };
  unstableActive: boolean;
  nowMs: number;
}): ConnectionHealthSnapshot => {
  const {
    socketStatus,
    sipStatus,
    socketReconnecting,
    isNetworkOnline,
    socketFlapTimestamps,
    sipFlapTimestamps,
    layerDownSinceMs,
    unstableActive,
    nowMs,
  } = params;

  const socketLayer = resolveSocketLayer(socketStatus, socketReconnecting);
  const sipLayer = resolveSipLayer(sipStatus);
  const networkLayer: ConnectionLayerHealth = isNetworkOnline ? "healthy" : "down";

  const socketFlapsLast60s = countFlapsInWindow(socketFlapTimestamps, nowMs);
  const sipFlapsLast60s = countFlapsInWindow(sipFlapTimestamps, nowMs);
  const totalFlapsLast60s = socketFlapsLast60s + sipFlapsLast60s;

  const lastDropAtMs = latestFlapTimestamp([
    ...socketFlapTimestamps,
    ...sipFlapTimestamps,
  ]);
  const lastDropSecondsAgo =
    lastDropAtMs != null
      ? Math.max(0, Math.floor((nowMs - lastDropAtMs) / 1000))
      : null;

  const canTakeCalls =
    isNetworkOnline &&
    socketStatus === "connected" &&
    sipStatus === "registered";

  const canGoAvailable = canTakeCalls;

  let status: ConnectionHealthStatus;

  if (!isNetworkOnline || socketLayer === "down" || sipLayer === "down") {
    status = "offline";
  } else if (socketLayer === "reconnecting" || sipLayer === "reconnecting") {
    status = "reconnecting";
  } else if (
    unstableActive &&
    socketLayer === "healthy" &&
    sipLayer === "healthy"
  ) {
    status = "unstable";
  } else {
    status = "online";
  }

  return {
    status,
    canTakeCalls,
    canGoAvailable,
    isNetworkOnline,
    layers: {
      dashboardSocket: socketLayer,
      sipWebphone: sipLayer,
      network: networkLayer,
    },
    totalFlapsLast60s,
    socketFlapsLast60s,
    sipFlapsLast60s,
    lastDropAtMs,
    lastDropSecondsAgo,
    unstableActive,
  };
};
